import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { SeasonProvider } from "@/lib/season";
import { AuthProvider, useAuth } from "@/lib/auth";
import { syncFavoritesWithCloud } from "@/lib/favorites";
import { SkeletonRows } from "@/components/ui/primitives";

const HomePage = lazy(() => import("@/pages/HomePage"));
const TeamsPage = lazy(() => import("@/pages/teams/TeamsPage"));
const TeamPage = lazy(() => import("@/pages/teams/TeamPage"));
const PlayersPage = lazy(() => import("@/pages/players/PlayersPage"));
const PlayerPage = lazy(() => import("@/pages/players/PlayerPage"));
const StaffPage = lazy(() => import("@/pages/StaffPage"));
const GamesPage = lazy(() => import("@/pages/games/GamesPage"));
const GamePage = lazy(() => import("@/pages/games/GamePage"));
const LivePage = lazy(() => import("@/pages/games/LivePage"));
const StandingsPage = lazy(() => import("@/pages/StandingsPage"));
const StatsPage = lazy(() => import("@/pages/StatsPage"));
const HistoryPage = lazy(() => import("@/pages/HistoryPage"));
const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
const RecordsPage = lazy(() => import("@/pages/RecordsPage"));
const ScoutingPage = lazy(() => import("@/pages/ScoutingPage"));
const ScoutingMethodPage = lazy(() => import("@/pages/ScoutingMethodPage"));
const ComparePage = lazy(() => import("@/pages/ComparePage"));
const SeasonsPage = lazy(() => import("@/pages/SeasonsPage"));
const SeasonPage = lazy(() => import("@/pages/SeasonPage"));
const FavoritesPage = lazy(() => import("@/pages/FavoritesPage"));
const MethodologyPage = lazy(() => import("@/pages/MethodologyPage"));
const AdminApp = lazy(() => import("@/pages/admin/AdminApp"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, retry: 1, refetchOnWindowFocus: false },
  },
});

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);
  return null;
}

function FavoritesCloudSync() {
  const { session } = useAuth();
  useEffect(() => {
    if (session) void syncFavoritesWithCloud();
  }, [session]);
  return null;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <SeasonProvider>
            <ScrollToTop />
            <FavoritesCloudSync />
            <AppShell>
              <Suspense fallback={<SkeletonRows rows={10} className="pt-10" />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/equipes" element={<TeamsPage />} />
                  <Route path="/equipes/:slug/*" element={<TeamPage />} />
                  <Route path="/joueurs" element={<PlayersPage />} />
                  <Route path="/joueurs/:id/*" element={<PlayerPage />} />
                  <Route path="/staff/:slug" element={<StaffPage />} />
                  <Route path="/direct" element={<LivePage />} />
                  <Route path="/matchs" element={<GamesPage />} />
                  <Route path="/matchs/:id" element={<GamePage />} />
                  <Route path="/classements" element={<StandingsPage />} />
                  <Route path="/statistiques/*" element={<StatsPage />} />
                  <Route path="/histoire" element={<HistoryPage />} />
                  <Route path="/histoire/:slug" element={<ArticlePage />} />
                  <Route path="/articles/:slug" element={<ArticlePage />} />
                  <Route path="/records/*" element={<RecordsPage />} />
                  <Route path="/scouting" element={<ScoutingPage />} />
                  <Route path="/scouting/methode" element={<ScoutingMethodPage />} />
                  <Route path="/comparateur" element={<ComparePage />} />
                  <Route path="/saisons" element={<SeasonsPage />} />
                  <Route path="/saisons/:year" element={<SeasonPage />} />
                  <Route path="/favoris" element={<FavoritesPage />} />
                  <Route path="/methodologie" element={<MethodologyPage />} />
                  <Route path="/admin/*" element={<AdminApp />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </AppShell>
          </SeasonProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
